import { useEffect, type ReactNode } from 'react'
import './ComingSoonPage.css'

/**
 * 「敬请期待」占位页
 *
 * 用途：定制板面业务尚未正式上线期间，作为 App 内「我的设计」入口的落地页
 * （Android 侧通过在线参数 `custom_deck_design_entry` 的 url 指到本页）。
 *
 * 约束：
 * - 纯展示页，不含任何跳转 / 提交，避免业务未上线时产生半成品链路；
 * - 关闭由原生容器顶栏的关闭按钮完成，页面不重复提供返回入口；
 * - 不依赖网络与登录态，无 token 也能正常展示；
 * - 版式一律用 Tailwind 工具类：本项目 Tailwind v4 的 preflight 被提升到 ID 级特异性，
 *   样式文件里的普通 class 选择器会被其 `margin/padding/font-size` 重置覆盖
 *   （参见 `fix(ImageEditor)` 那次踩坑），故 .css 只放背景装饰与 keyframes 动效。
 */

const CONTENT = {
  badge: '定制业务 · 筹备中',
  eyebrow: 'COMING SOON',
  title: '敬请期待',
  subtitle: '定制板面正在做最后的打磨',
  desc: '很快你就能把自己的图案印上板面，实时预览 3D 效果，一键下单制作。',
  features: [
    { label: '上传图案', icon: 'upload' },
    { label: '3D 预览', icon: 'cube' },
    { label: '一键制作', icon: 'bolt' },
  ] as const,
  footer: '上线后会在「我的设计」第一时间告诉你',
}

/** 板面轮廓（顶视图，340 × 100 画布，板身 296 × 60，约 4.9 : 1 的板身长宽比） */
const DECK_PATH =
  'M46 20H294C308 20 318 33 318 50C318 67 308 80 294 80H46C32 80 22 67 22 50C22 33 32 20 46 20Z'

/** 桥钉孔（两块桥各 4 个孔），板面上最容易被一眼认出"这是滑板"的细节 */
const DECK_HOLES = [
  [64, 42],
  [64, 58],
  [86, 42],
  [86, 58],
  [254, 42],
  [254, 58],
  [276, 42],
  [276, 58],
] as const

const FEATURE_ICONS: Record<string, ReactNode> = {
  upload: (
    <>
      <path d="M12 16V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </>
  ),
  cube: (
    <>
      <path d="M12 3 20 7.5v9L12 21 4 16.5v-9L12 3Z" />
      <path d="M4 7.5 12 12l8-4.5" />
      <path d="M12 12v9" />
    </>
  ),
  bolt: <path d="M13 3 5 14h6l-1 7 8-11h-6l1-7Z" />,
}

export function ComingSoonPage() {
  useEffect(() => {
    document.title = '敬请期待'
  }, [])

  return (
    <div className="relative isolate flex flex-1 flex-col overflow-hidden bg-[#07070d] px-5 pt-[calc(env(safe-area-inset-top,0px)+20px)] pb-[calc(env(safe-area-inset-bottom,0px)+24px)] text-white">
      {/* 背景光效 */}
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
        <div className="cs-glow cs-glow--top" />
        <div className="cs-glow cs-glow--bottom" />
        <div className="cs-grid" />
        <div className="cs-vignette" />
      </div>

      <main className="relative z-[1] m-auto flex w-full max-w-[330px] flex-col items-center text-center">
        <span
          className="cs-rise inline-flex items-center gap-[7px] rounded-full border border-white/10 bg-white/5 px-[11px] py-[5px] text-[11px] tracking-[0.06em] text-white/60 backdrop-blur-md"
          style={{ animationDelay: '0ms' }}
        >
          <span className="cs-dot h-[5px] w-[5px] rounded-full bg-violet-400" />
          {CONTENT.badge}
        </span>

        {/* 板面主视觉 */}
        <div
          className="cs-rise relative mt-[clamp(16px,3.6vh,30px)] w-[min(80vw,318px,52vh)] pb-[10px]"
          style={{ animationDelay: '90ms' }}
        >
          <div className="cs-deck drop-shadow-[0_12px_16px_rgba(79,70,229,0.22)]">
            <svg className="block h-auto w-full overflow-visible" viewBox="0 0 340 100" role="img" aria-label="定制板面示意">
              <defs>
                <linearGradient id="csFace" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="48%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
                <linearGradient id="csShine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
                  <stop offset="50%" stopColor="#ffffff" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="csSheen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.26" />
                  <stop offset="42%" stopColor="#ffffff" stopOpacity="0" />
                  <stop offset="100%" stopColor="#000000" stopOpacity="0.22" />
                </linearGradient>
                <pattern id="csLines" width="9" height="9" patternUnits="userSpaceOnUse" patternTransform="rotate(38)">
                  <line x1="0" y1="0" x2="0" y2="9" stroke="#ffffff" strokeOpacity="0.16" strokeWidth="1.4" />
                </pattern>
                <clipPath id="csClip">
                  <path d={DECK_PATH} />
                </clipPath>
                <filter id="csGlow" x="-40%" y="-80%" width="180%" height="260%">
                  <feGaussianBlur stdDeviation="8" />
                </filter>
              </defs>

              {/* 整体轻微倾斜，避免"平放"的死板 */}
              <g transform="rotate(-5 170 50)">
                {/* 外发光 */}
                <path d={DECK_PATH} fill="url(#csFace)" opacity="0.34" filter="url(#csGlow)" />

                {/* 板面本体 */}
                <path d={DECK_PATH} fill="url(#csFace)" />
                <path d={DECK_PATH} fill="url(#csLines)" />
                <path d={DECK_PATH} fill="url(#csSheen)" />
                <path d={DECK_PATH} fill="none" stroke="#ffffff" strokeOpacity="0.3" strokeWidth="1.1" />

                {/* 内框虚线：待上传的图案区域 */}
                <rect
                  x="46"
                  y="27"
                  width="248"
                  height="46"
                  rx="13"
                  fill="none"
                  stroke="#ffffff"
                  strokeOpacity="0.42"
                  strokeWidth="1.3"
                  strokeDasharray="6 7"
                  strokeLinecap="round"
                />

                {/* 桥钉孔 */}
                <g fill="#ffffff" fillOpacity="0.34">
                  {DECK_HOLES.map(([cx, cy]) => (
                    <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2.4" />
                  ))}
                </g>

                {/* 高光扫过 */}
                <g clipPath="url(#csClip)">
                  <rect className="cs-shine" x="-80" y="0" width="80" height="100" fill="url(#csShine)" />
                </g>
              </g>
            </svg>
          </div>
          <div
            className="absolute inset-x-[14%] bottom-[2px] h-3 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,rgba(139,92,246,0.5),rgba(139,92,246,0)_72%)] blur-[9px]"
            aria-hidden="true"
          />
        </div>

        <p
          className="cs-rise mt-[clamp(18px,3.8vh,32px)] mb-2 text-[10px] font-semibold tracking-[0.44em] indent-[0.44em] text-white/30"
          style={{ animationDelay: '170ms' }}
        >
          {CONTENT.eyebrow}
        </p>

        <h1
          className="cs-rise m-0 bg-linear-to-b from-white to-white/60 bg-clip-text text-[min(31px,5.4vh)] leading-[1.25] font-bold tracking-[0.14em] indent-[0.14em] text-transparent"
          style={{ animationDelay: '230ms' }}
        >
          {CONTENT.title}
        </h1>

        <p
          className="cs-rise mt-[clamp(8px,1.5vh,12px)] text-[13px] leading-[1.5] tracking-[0.02em] text-white/60"
          style={{ animationDelay: '290ms' }}
        >
          {CONTENT.subtitle}
        </p>

        <p
          className="cs-rise mt-2 max-w-[17em] text-[12px] leading-[1.75] text-white/40"
          style={{ animationDelay: '340ms' }}
        >
          {CONTENT.desc}
        </p>

        <ul
          className="cs-rise mt-[clamp(14px,2.4vh,20px)] flex list-none flex-wrap justify-center gap-2 p-0"
          style={{ animationDelay: '400ms' }}
        >
          {CONTENT.features.map((item) => (
            <li
              key={item.label}
              className="inline-flex items-center gap-[5px] rounded-lg border border-white/[0.08] bg-white/[0.04] px-2.5 py-1.5 text-[11px] tracking-[0.02em] whitespace-nowrap text-white/55"
            >
              <svg
                className="h-3 w-3 opacity-65"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {FEATURE_ICONS[item.icon]}
              </svg>
              {item.label}
            </li>
          ))}
        </ul>

        <p
          className="cs-footer cs-rise mt-[clamp(16px,3.3vh,28px)] w-full pt-4 text-[10.5px] tracking-[0.06em] text-white/25"
          style={{ animationDelay: '470ms' }}
        >
          {CONTENT.footer}
        </p>
      </main>
    </div>
  )
}

export default ComingSoonPage
